import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const TaskBoard = () => {
  const { tasks, projects, users, createTask, updateTask } = useData();
  const { currentUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    assigneeId: '',
    priority: 'medium',
    dueDate: '',
    estimatedHours: ''
  });

  // Filter projects based on current user role
  const availableProjects = useMemo(() => {
    if (!currentUser) return [];

    if (currentUser.role === 'owner') {
      return projects;
    } else if (currentUser.role === 'project_manager') {
      // Show only projects managed by this project manager
      return projects.filter(project => project.managerId === currentUser.id);
    }
    return [];
  }, [projects, currentUser]);

  // Filter assignees based on selected project
  const availableAssignees = useMemo(() => {
    if (!formData.projectId) {
      return users.filter(u => u.role === 'employee');
    }

    const selectedProject = projects.find(p => p.id === formData.projectId);
    if (!selectedProject || !selectedProject.teamMembers) {
      return users.filter(u => u.role === 'employee');
    }

    // Filter employees who are part of the selected project's team
    return users.filter(u => 
      u.role === 'employee' && 
      selectedProject.teamMembers.includes(u.id)
    );
  }, [formData.projectId, projects, users]);

  // Filter tasks based on current user role and project management
  const filteredTasks = useMemo(() => {
    if (!currentUser) return [];

    if (currentUser.role === 'employee') {
      return tasks.filter(t => t.assigneeId === currentUser.id);
    } else if (currentUser.role === 'project_manager') {
      // Show only tasks from projects managed by this project manager
      const managedProjectIds = availableProjects.map(p => p.id);
      return tasks.filter(t => managedProjectIds.includes(t.projectId));
    } else if (currentUser.role === 'owner') {
      return tasks;
    }

    return [];
  }, [tasks, currentUser, availableProjects]);

  const taskColumns = {
    not_started: filteredTasks.filter(t => t.status === 'not_started'),
    ongoing: filteredTasks.filter(t => t.status === 'ongoing'),
    completed: filteredTasks.filter(t => t.status === 'completed')
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const assignee = users.find(u => u.id === formData.assigneeId);
    const project = projects.find(p => p.id === formData.projectId);

    const taskData = {
      ...formData,
      assigneeName: assignee?.name || '',
      projectName: project?.name || '',
      status: 'not_started',
      estimatedHours: parseInt(formData.estimatedHours) || 0,
      completedHours: 0,
      createdBy: currentUser.id, // Track who created the task
      projectId: formData.projectId
    };

    await createTask(taskData);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      projectId: '',
      assigneeId: '',
      priority: 'medium',
      dueDate: '',
      estimatedHours: ''
    });
    setShowCreateForm(false);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    await updateTask(taskId, { status: newStatus });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent'
    };
    return colors[priority] || 'priority-medium';
  };

  // Clear assignee when project changes
  const handleProjectChange = (e) => {
    setFormData({
      ...formData, 
      projectId: e.target.value,
      assigneeId: '' // Clear assignee when project changes
    });
  };

  const TaskCard = ({ task }) => (
    <div className="task-card">
      <div className="task-header">
        <h4 className="task-title">{task.title}</h4>
        <span className={`priority-badge ${getPriorityColor(task.priority)}`}>
          {task.priority}
        </span>
      </div>

      <p className="task-description">{task.description}</p>

      <div className="task-meta">
        <div className="task-assignee">
          <i className="fas fa-user"></i>
          <span className="meta-text">{task.assigneeName}</span>
        </div>
        <div className="task-project">
          <i className="fas fa-briefcase"></i>
          <span className="meta-text">{task.projectName}</span>
        </div>
        <div className="task-due-date">
          <i className="fas fa-calendar"></i>
          <span className="meta-text">{new Date(task.dueDate).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="task-progress">
        <div className="progress-info">
          <span className="progress-text">Progress: {task.completedHours || 0} / {task.estimatedHours || 0} hrs</span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ 
              width: `${task.estimatedHours > 0 ? ((task.completedHours || 0) / task.estimatedHours) * 100 : 0}%` 
            }}
          ></div>
        </div>
      </div>

      <div className="task-actions">
        {task.status === 'not_started' && (
          <button 
            onClick={() => handleStatusChange(task.id, 'ongoing')}
            className="btn btn-sm btn-primary"
          >
            <i className="fas fa-play"></i>
            Start Task
          </button>
        )}
        {task.status === 'ongoing' && (
          <button 
            onClick={() => handleStatusChange(task.id, 'completed')}
            className="btn btn-sm btn-success"
          >
            <i className="fas fa-check"></i>
            Complete
          </button>
        )}
        {task.status === 'completed' && (
          <span className="task-completed">
            <i className="fas fa-check-circle"></i>
            Completed
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="task-board">
      <div className="page-header">
        <div className="header-content">
          <h2>Task Management</h2>
          <p>Organize and track project tasks</p>
        </div>
        {(currentUser?.role === 'owner' || currentUser?.role === 'project_manager') && (
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i>
            New Task
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Task</h3>
              <button onClick={resetForm} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label className="form-label">Task Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Enter task title"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  placeholder="Describe the task requirements"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <select
                    className="form-control"
                    value={formData.projectId}
                    onChange={handleProjectChange}
                    required
                  >
                    <option value="">Select Project</option>
                    {availableProjects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                  {availableProjects.length === 0 && (
                    <small className="form-helper">No projects available to assign tasks</small>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Assignee</label>
                  <select
                    className="form-control"
                    value={formData.assigneeId}
                    onChange={(e) => setFormData({...formData, assigneeId: e.target.value})}
                    required
                    disabled={!formData.projectId}
                  >
                    <option value="">
                      {!formData.projectId ? "Select project first" : "Select team member"}
                    </option>
                    {availableAssignees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name}
                      </option>
                    ))}
                  </select>
                  {formData.projectId && availableAssignees.length === 0 && (
                    <small className="form-helper">No team members assigned to this project</small>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-control"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  >
                    <option value="low">🟢 Low</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="high">🟠 High</option>
                    <option value="urgent">🔴 Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Estimated Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData({...formData, estimatedHours: e.target.value})}
                    min="1"
                    max="1000"
                    placeholder="Hours needed"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="kanban-board">
        <div className="kanban-column">
          <div className="column-header">
            <h3>To Do</h3>
            <span className="task-count">{taskColumns.not_started.length}</span>
          </div>
          <div className="column-content">
            {taskColumns.not_started.length > 0 ? (
              taskColumns.not_started.map(task => (
                <TaskCard key={task.id} task={task} />
              ))
            ) : (
              <div className="no-tasks">
                <i className="fas fa-clipboard-list"></i>
                <p>No tasks to start</p>
              </div>
            )}
          </div>
        </div>

        <div className="kanban-column">
          <div className="column-header">
            <h3>In Progress</h3>
            <span className="task-count">{taskColumns.ongoing.length}</span>
          </div>
          <div className="column-content">
            {taskColumns.ongoing.length > 0 ? (
              taskColumns.ongoing.map(task => (
                <TaskCard key={task.id} task={task} />
              ))
            ) : (
              <div className="no-tasks">
                <i className="fas fa-clock"></i>
                <p>No tasks in progress</p>
              </div>
            )}
          </div>
        </div>

        <div className="kanban-column">
          <div className="column-header">
            <h3>Completed</h3>
            <span className="task-count">{taskColumns.completed.length}</span>
          </div>
          <div className="column-content">
            {taskColumns.completed.length > 0 ? (
              taskColumns.completed.map(task => (
                <TaskCard key={task.id} task={task} />
              ))
            ) : (
              <div className="no-tasks">
                <i className="fas fa-check-circle"></i>
                <p>No completed tasks</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {filteredTasks.length === 0 && (
        <div className="no-data">
          <i className="fas fa-tasks"></i>
          <h3>No Tasks Found</h3>
          <p>
            {currentUser?.role === 'project_manager' 
              ? 'Create your first task to get started.' 
              : 'No tasks assigned to you yet.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TaskBoard;